'use client';

import { useEffect } from 'react';

/**
 * z-index tiers for the dashboard's fixed-position overlays, so a new modal
 * picks a tier instead of inventing a fresh magic number.
 *
 *   base    z-50   — the default: a single modal, nothing else open behind it
 *   stacked z-[60] — opened over another already-open surface (e.g. a toast rail)
 *   confirm z-[70] — confirmation dialogs, above stacked modals
 *   system  z-[100] — reserved for TwitchReauthWall (components/twitch-reauth-wall.js),
 *                      a hard non-dismissible block. It does NOT use this component —
 *                      its whole point is that it can't be closed — but claims the top
 *                      tier so nothing dismissible can ever render above it.
 */
export const MODAL_Z = {
  base: 'z-50',
  stacked: 'z-[60]',
  confirm: 'z-[70]',
  system: 'z-[100]',
};

/**
 * Shared modal shell — centered backdrop + card, Escape-to-close, and
 * backdrop-click-to-close, all funneled through `onClose`. Card styling
 * (border, rounding, header/footer layout) stays with each caller since it
 * varies too much to usefully unify; this only owns the mechanics every modal
 * needs.
 *
 * `closeOnBackdrop`/`closeOnEscape` default to true; pass `false` explicitly
 * for a deliberately non-dismissible flow (e.g. SupportModal while composing
 * a report) rather than relying on the default.
 */
export function Modal({
  onClose,
  children,
  z = 'base',
  backdropClassName = 'bg-black/60',
  className = 'w-full max-w-lg',
  closeOnBackdrop = true,
  closeOnEscape = true,
  ariaLabel,
}) {
  useEffect(() => {
    if (!closeOnEscape) return;
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeOnEscape, onClose]);

  return (
    <div
      className={`fixed inset-0 ${MODAL_Z[z]} flex items-center justify-center ${backdropClassName} p-4`}
      onClick={closeOnBackdrop ? onClose : undefined}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
    >
      <div className={className} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}
